import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { carouselImages } from "./CarouselData";

const GOLD = "#ffc000";
const PRIMARY = "#000c66";
const SLIDES = carouselImages;
const DURATION = 6000;

// Decorative corner bracket
const Corner = ({ style }) => (
  <span
    style={{
      position: "absolute",
      width: 24,
      height: 24,
      ...style,
    }}
  />
);

export const HeroSection = () => {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progRef = useRef(null);
  const progStart = useRef(Date.now());
  const navigate = useNavigate();

  const goTo = (index) => {
    setActive(index);
    setProgress(0);
    progStart.current = Date.now();
  };

  // Autoplay
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % SLIDES.length);
      setProgress(0);
      progStart.current = Date.now();
    }, DURATION);
    return () => clearInterval(timerRef.current);
  }, []);

  // Progress bar
  useEffect(() => {
    progRef.current = requestAnimationFrame(function tick() {
      const elapsed = Date.now() - progStart.current;
      setProgress(Math.min((elapsed / DURATION) * 100, 100));
      progRef.current = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(progRef.current);
  }, [active]);

  const slide = SLIDES[active];

  const handleCTA = () => {
    if (slide.path.startsWith("http")) {
      window.open(slide.path, "_blank", "noopener,noreferrer");
    } else {
      navigate(slide.path);
    }
  };

  return (
    <>
      <style>{`
        @keyframes kenBurns {
          from { transform: scale(1); }
          to   { transform: scale(1.08); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes goldPulse {
          0%,100% { opacity: 0.07; }
          50%      { opacity: 0.13; }
        }
        .hero-slide-img {
          animation: kenBurns ${DURATION}ms ease-out forwards;
        }
        .hero-title   { animation: fadeSlideUp 0.7s 0.1s ease both; }
        .hero-quote   { animation: fadeSlideUp 0.7s 0.3s ease both; }
        .hero-cta     { animation: fadeSlideUp 0.7s 0.5s ease both; }
        .hero-cross   { animation: goldPulse 4s ease-in-out infinite; }
      `}</style>

      <section
        style={{
          position: "relative",
          width: "100%",
          height: "100vh",
          minHeight: 560,
          overflow: "hidden",
          background: PRIMARY,
        }}
      >
        {/* ── Background image (Ken Burns) ─────────────────── */}
        {SLIDES.map((s, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${s.img})`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
              opacity: i === active ? 1 : 0,
              transition: "opacity 1s ease",
              zIndex: 1,
            }}
          >
            {i === active && (
              <div className="hero-slide-img" style={{ position: "absolute", inset: 0, backgroundImage: `url(${s.img})`, backgroundSize: "cover", backgroundPosition: "center top" }} />
            )}
          </div>
        ))}

        {/* ── Spotlight overlay ────────────────────────────── */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse 70% 80% at 50% 40%, rgba(0,12,102,0.45) 0%, rgba(0,0,0,0.72) 55%, rgba(0,0,0,0.92) 100%)",
            zIndex: 2,
          }}
        />

        {/* ── Large decorative cross watermark ─────────────── */}
        <svg
          className="hero-cross"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -58%)",
            width: "min(420px, 80vw)",
            height: "min(420px, 80vw)",
            zIndex: 3,
            pointerEvents: "none",
          }}
          viewBox="0 0 200 200"
          fill="none"
        >
          {/* Cross shape */}
          <rect x="88" y="10" width="24" height="180" rx="4" fill={GOLD} />
          <rect x="10" y="68" width="180" height="24" rx="4" fill={GOLD} />
          {/* Halo ring */}
          <circle cx="100" cy="100" r="94" stroke={GOLD} strokeWidth="1.5" strokeDasharray="6 6" />
          <circle cx="100" cy="100" r="80" stroke={GOLD} strokeWidth="0.5" />
          {/* Corner stars */}
          {[[20,20],[180,20],[20,180],[180,180]].map(([x,y],i) => (
            <text key={i} x={x} y={y} fill={GOLD} fontSize="10" textAnchor="middle" dominantBaseline="middle">✦</text>
          ))}
        </svg>

        {/* ── Content ──────────────────────────────────────── */}
        <div
          style={{ position: "absolute", inset: 0, zIndex: 4 }}
          className="flex flex-col items-center justify-center px-4 text-center"
        >
          {/* Church badge */}
          <div
            className="flex items-center gap-2 mb-6 px-5 py-1.5 rounded-full"
            style={{
              border: `1px solid ${GOLD}55`,
              background: "rgba(0,12,102,0.5)",
              backdropFilter: "blur(6px)",
            }}
          >
            <span style={{ color: GOLD, fontSize: 10 }}>✦</span>
            <span className="font-abel uppercase tracking-[0.35em] text-xs" style={{ color: GOLD }}>
              CCC Salvation Parish
            </span>
            <span style={{ color: GOLD, fontSize: 10 }}>✦</span>
          </div>

          {/* Title */}
          <h1
            key={`title-${active}`}
            className="hero-title font-aref font-bold text-white leading-tight"
            style={{
              fontSize: "clamp(2rem, 6vw, 4.5rem)",
              textShadow: "0 4px 24px rgba(0,0,0,0.8)",
              maxWidth: 800,
            }}
          >
            {slide.title || "Welcome to Salvation Parish"}
          </h1>

          {/* Divider */}
          <div
            className="my-5 flex items-center gap-3"
            style={{ width: "min(240px, 60vw)" }}
          >
            <div className="flex-1 h-px" style={{ background: `${GOLD}66` }} />
            <svg viewBox="0 0 24 24" width={14} height={14} fill={GOLD}>
              <path d="M9 0h6v7h9v6h-9v11H9V13H0V7h9z" />
            </svg>
            <div className="flex-1 h-px" style={{ background: `${GOLD}66` }} />
          </div>

          {/* Scripture quote */}
          {slide.quote && (
            <p
              key={`quote-${active}`}
              className="hero-quote font-abel italic text-center"
              style={{
                color: "rgba(255,255,255,0.85)",
                fontSize: "clamp(0.95rem, 2.2vw, 1.3rem)",
                maxWidth: 640,
                lineHeight: 1.7,
                textShadow: "0 2px 12px rgba(0,0,0,0.7)",
              }}
            >
              "{slide.quote}"
            </p>
          )}

          {/* CTA */}
          <div key={`cta-${active}`} className="hero-cta mt-8">
            <button
              onClick={handleCTA}
              className="font-abel font-bold uppercase tracking-widest text-sm px-10 py-4 rounded-full transition-all duration-300 hover:scale-105"
              style={{
                background: GOLD,
                color: PRIMARY,
                boxShadow: `0 8px 32px ${GOLD}55, 0 0 0 3px ${GOLD}33`,
              }}
            >
              {slide.label}
            </button>
          </div>
        </div>

        {/* ── Slide dots ───────────────────────────────────── */}
        <div
          style={{ position: "absolute", bottom: 48, left: "50%", transform: "translateX(-50%)", zIndex: 5 }}
          className="flex items-center gap-3"
        >
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              style={{
                width: i === active ? 32 : 8,
                height: 8,
                borderRadius: 4,
                background: i === active ? GOLD : "rgba(255,255,255,0.4)",
                border: "none",
                cursor: "pointer",
                transition: "all 0.35s ease",
                padding: 0,
              }}
            />
          ))}
        </div>

        {/* ── Progress bar ─────────────────────────────────── */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.1)",
            zIndex: 5,
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${GOLD}aa, ${GOLD})`,
              transition: "width 0.1s linear",
            }}
          />
        </div>

        {/* ── Decorative gold corner frames ────────────────── */}
        {[
          { top: 20, left: 20, borderTop: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}` },
          { top: 20, right: 20, borderTop: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}` },
          { bottom: 20, left: 20, borderBottom: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}` },
          { bottom: 20, right: 20, borderBottom: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}` },
        ].map((s, i) => (
          <Corner key={i} style={{ ...s, zIndex: 5 }} />
        ))}
      </section>
    </>
  );
};
